<template>
    <div class="modal fade" id="pagoAsignacionModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-lg">
            <div class="modal-content">
                <!-- Cabecera -->
                <div class="modal-header">
                    <h4 class="modal-title">Asignar Ventas al Pago</h4>
                    <button type="button" class="close" data-dismiss="modal" @click="cerrarModal">
                        &times;
                    </button>
                </div>

                <!-- Cuerpo -->
                <div class="modal-body">
                    <!-- Filas de asignaciones -->
                    <div v-for="(asignacion, index) in asignaciones" :key="index" class="form-group row mb-2">
                        <div class="col-md-6">
                            <select class="form-control" v-model="asignacion.venta_id"
                                @change="onVentaSelected(index, asignacion.venta_id)">
                                <option disabled value="">Seleccione una venta</option>
                                <option v-for="venta in ventasPendientes" :key="venta.id" :value="venta.id"
                                    :disabled="yaSeleccionada(venta.id) && venta.id !== asignacion.venta_id">
                                    {{ venta.numcontrol }} - Saldo: {{ formatNumber(venta.saldo) }}
                                </option>
                            </select>
                        </div>

                        <div class="col-md-6">
                            <input type="number" class="form-control" v-model.number="asignacion.monto_aplicado"
                                placeholder="Monto a aplicar" step="0.01" :max="maxMontoAsignacion(index)" />
                        </div>
                    </div>

                    <!-- Botón para agregar otra asignación -->
                    <div class="mt-2" v-if="saldoRestante > 0 && availableVentas.length > 0">
                        <button class="btn btn-primary" type="button" @click="agregarAsignacion">
                            Agregar otra venta
                        </button>
                    </div>

                    <!-- Información de totales -->
                    <div class="mt-3">
                        <p><strong>Total asignado (nuevo):</strong> {{ formatNumber(totalAsignadoNew) }}</p>
                        <p><strong>Total aplicado (existente):</strong> {{ formatNumber(existingApplied) }}</p>
                        <p><strong>Saldo restante del pago:</strong> {{ formatNumber(saldoRestante) }}</p>
                    </div>
                </div>

                <!-- Pie del modal -->
                <div class="modal-footer">
                    <button class="btn btn-success" @click="guardarAsignaciones" type="button">Guardar</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import api from "@/services/api";

export default {
    name: "PagoVentaModal",
    props: {
        pago: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            ventasPendientes: [], // ventas filtradas y ordenadas
            asignaciones: [] // Cada objeto: { venta_id: "", monto_aplicado: 0 }
        };
    },
    computed: {
        // Suma total de los montos asignados en las filas nuevas del modal
        totalAsignadoNew() {
            return this.asignaciones.reduce((sum, a) => sum + (Number(a.monto_aplicado) || 0), 0);
        },
        // Monto ya aplicado en la base (registros existentes de pago_ventas vinculados a este pago)
        existingApplied() {
            if (!this.pago || !this.pago.pago_ventas) return 0;
            return this.pago.pago_ventas.reduce((total, registro) => {
                return total + (Number(registro.monto_aplicado) || 0);
            }, 0);
        },
        // Saldo restante global del pago: montoPago - (aplicado existente + asignado nuevo)
        saldoRestante() {
            const montoPago = parseFloat(this.pago.monto || 0);
            const restante = montoPago - this.existingApplied - this.totalAsignadoNew;
            // Evitar negativos
            return Math.max(0, Number(restante.toFixed(2)));
        },
        // Ventas que aún no se han seleccionado en las filas nuevas
        availableVentas() {
            const seleccionadas = this.asignaciones
                .map(a => a.venta_id)
                .filter(id => id);
            return this.ventasPendientes.filter(venta => !seleccionadas.includes(venta.id));
        }
    },
    methods: {
        // Carga ventas pendientes desde el endpoint y filtra/ordena en cliente
        async cargarVentasPendientes() {
            if (!this.pago || !this.pago.cliente_id) {
                this.ventasPendientes = [];
                return;
            }

            try {
                const response = await api.get(`/clientes/${this.pago.cliente_id}/ventas-pendientes`);
                const raw = Array.isArray(response.data) ? response.data : [];
                console.log("Ventas pendientes crudas:", raw);
                // Filtrar ventas cuyo status_logistico no sea 'anulado' y ordenar por fecha_venta asc (más vieja primero)
                const ventas = raw
                    .filter(v => String(v.status_logistico || '').toLowerCase() !== 'anulado')
                    .map(v => {
                        // Asegurar que exista saldo en número (si backend no lo provee, calcularlo)
                        const saldo = (typeof v.saldo !== 'undefined') ? Number(v.saldo) : Number((v.total || 0) - (v.pagado || 0));
                        return {
                            ...v,
                            saldo: Number(isNaN(saldo) ? 0 : saldo)
                        };
                    })
                    .sort((a, b) => {
                        const fa = a.fecha_venta || a.fecha_entrega || null;
                        const fb = b.fecha_venta || b.fecha_entrega || null;
                        if (!fa && !fb) return 0;
                        if (!fa) return 1;
                        if (!fb) return -1;
                        return new Date(fa) - new Date(fb); // ascendente: más vieja primero
                    });

                this.ventasPendientes = ventas;
            } catch (error) {
                console.error("Error al cargar ventas pendientes:", error.response ? error.response.data : error.message);
                this.ventasPendientes = [];
            }
        },
        agregarAsignacion() {
            // Solo agregar si hay saldo restante y ventas disponibles
            if (this.saldoRestante > 0 && this.availableVentas.length > 0) {
                this.asignaciones.push({ venta_id: "", monto_aplicado: 0 });
            }
        },
        getVentaSaldo(venta_id) {
            const venta = this.ventasPendientes.find(v => v.id === venta_id);
            return venta ? Number(venta.saldo || 0) : 0;
        },
        yaSeleccionada(venta_id) {
            return this.asignaciones.some(a => a.venta_id === venta_id);
        },
        onVentaSelected(index, venta_id) {
            // Si al seleccionar una venta no hay monto asignado, prellenar con el máximo posible
            if (!venta_id) return;
            const asignacion = this.asignaciones[index];
            if (!asignacion) return;

            // Si monto_aplicado es 0, sugerimos el mínimo entre saldo de la venta y saldoRestante actual
            if (!asignacion.monto_aplicado || Number(asignacion.monto_aplicado) === 0) {
                const saldoVenta = this.getVentaSaldo(venta_id);
                // calcular disponible para esta fila considerando otras filas
                const max = this.maxMontoAsignacion(index);
                asignacion.monto_aplicado = Math.min(saldoVenta, max);
            }
        },
        // Retorna el máximo permitido para la asignación de la fila indicada.
        maxMontoAsignacion(index) {
            const current = Number(this.asignaciones[index]?.monto_aplicado || 0);
            // Suma total asignado excepto el valor de la fila actual.
            const totalExcludingCurrent = this.totalAsignadoNew - current;
            // El monto disponible para la fila es el monto del pago menos lo asignado en otras filas y lo ya aplicado
            const montoPago = parseFloat(this.pago.monto || 0);
            const disponiblePago = montoPago - this.existingApplied - totalExcludingCurrent;
            const saldoVenta = this.getVentaSaldo(this.asignaciones[index]?.venta_id);
            const max = Math.min(Number(saldoVenta || 0), Number(Math.max(0, disponiblePago)));
            // Evitar negativos
            return Number(max.toFixed(2));
        },
        cerrarModal() {
            this.$emit('cerrar');
        },
        async guardarAsignaciones() {
            // Validación: total asignado nuevo no debe exceder el saldo restante disponible
            if (this.totalAsignadoNew > (parseFloat(this.pago.monto || 0) - this.existingApplied)) {
                alert("El total de los montos asignados no puede ser mayor al saldo disponible del pago.");
                return;
            }

            // Filtrar solo las asignaciones donde se haya seleccionado una venta y monto > 0
            const payloads = this.asignaciones
                .filter(a => a.venta_id && Number(a.monto_aplicado) > 0)
                .map(asignacion => ({
                    pago_id: this.pago.id,
                    venta_id: asignacion.venta_id,
                    monto_aplicado: Number(asignacion.monto_aplicado).toFixed(2)
                }));

            if (payloads.length === 0) {
                alert("Debe asignar al menos una venta con monto mayor a 0.");
                return;
            }

            try {
                // Ejecutamos todas las peticiones concomitantemente.
                const posts = payloads.map(data => api.post("/pagoventas", data));
                const responses = await Promise.all(posts);

                // Emitir evento con respuestas y cerrar modal
                this.$emit("guardado", responses.map(r => r.data ? r.data : r));
                this.cerrarModal();
            } catch (error) {
                console.error("Error al guardar asignaciones:", error.response ? error.response.data : error.message);
                alert("Hubo un error al guardar las asignaciones. Por favor, intente de nuevo.");
            }
        },
        formatNumber(value) {
            const n = Number(value || 0);
            return n.toFixed(2);
        }
    },
    watch: {
        pago(newPago) {
            if (newPago && newPago.cliente_id) {
                this.cargarVentasPendientes();
                // Reiniciar asignaciones: si el pago ya tiene pagos aplicados, no prellenamos aquí
                this.asignaciones = [];
                this.agregarAsignacion();
            } else {
                this.ventasPendientes = [];
                this.asignaciones = [];
            }
        }
    },
    mounted() {
        if (this.pago && this.pago.cliente_id) {
            this.cargarVentasPendientes();
            this.asignaciones = [];
            this.agregarAsignacion();
        }
    }
};
</script>

<style scoped>
.modal-body { max-height: 60vh; overflow-y: auto; }
</style>